import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const C = {
  navy: '#122326',
  slate: '#213A3E',
  gold: '#BF6847',
  goldLight: '#DBDDAC',
  cream: '#F0EFEB',
  warm: '#F7F6F3',
  sand: '#E2E0DB',
  white: '#FFFFFF',
};

const FF = {
  display: "'Libre Baskerville', Georgia, serif",
  body: "'Nunito', 'Segoe UI', sans-serif",
};

const steps = [
  {
    num: '1',
    title: 'Complete the readiness questionnaire',
    desc: 'A short intake so we can understand your timeline, goals, and situation before we connect.',
  },
  {
    num: '2',
    title: 'Free confidential consultation',
    desc: 'A PADD advisor reviews your responses and connects you with the right specialists — NBDC, SCORE, REAP, legal, and financial.',
  },
  {
    num: '3',
    title: 'Build your transition plan',
    desc: 'We help develop a step-by-step roadmap: valuation, successor identification, financing, legal structure, and timeline.',
  },
  {
    num: '4',
    title: 'Connect to buyers and resources',
    desc: "Through PADD's Panhandle Business Succession Registry, we match retiring owners with potential buyers regionwide.",
  },
];

const timeline = [
  {
    label: '5+ years out',
    color: C.sand,
    textColor: C.slate,
    items: 'Clarify goals · Assemble advisors · Document operations · Identify successors · Build value',
  },
  {
    label: '2–3 years out',
    color: '#D6E8F5',
    textColor: '#0C447C',
    items: 'Get valuation · Choose exit type · Secure financing · Prepare legal documents',
  },
  {
    label: 'Within 1 year',
    color: '#DDF0C8',
    textColor: '#27500A',
    items: 'Finalize terms · Train successor · Communicate with staff & customers',
  },
  {
    label: 'At transition',
    color: '#FAE8C0',
    textColor: '#633806',
    items: 'Complete handover · Mentor new owner · Celebrate your legacy',
  },
];

const resources = [
  { initials: 'NB', name: 'Nebraska Business Development Center (NBDC)', desc: "Free, confidential consulting on succession planning, valuations, and lender introductions. PADD's primary transition partner." },
  { initials: 'SC', name: 'SCORE — Mentor Network', desc: 'Volunteer mentors with decades of experience offering free advice on succession, valuation, and exit strategy.' },
  { initials: 'RP', name: 'Rural Enterprise Assistance Project (REAP)', desc: 'Technical assistance for rural business owners navigating ownership transitions, including cooperative conversions.' },
  { initials: 'SB', name: 'U.S. Small Business Administration (SBA)', desc: 'Valuation guidance, succession loan programs, and the Close or Sell Your Business guide.' },
  { initials: 'ES', name: 'ESOP Association — Iowa/Nebraska Chapter', desc: 'Connect with ESOP specialists if employee ownership might be the right path for your business.' },
  { initials: 'EX', name: 'Nebraska Extension', desc: 'Research-backed resources on rural business succession and retaining rural businesses for communities statewide.' },
];

const checklistItems = [
  '3+ years of business financial records (P&L, tax returns)',
  'Rough estimate of what my business is worth',
  'Key business processes and procedures documented',
  'List of key clients, vendors, and contacts',
  'Personal retirement income needs calculated',
  'At least one possible successor or buyer identified',
  'An accountant or CPA who can join this process',
  'An attorney for business documents',
  'Family conversation about the transition started',
  'Buy-sell agreement in place (if applicable)',
];

const questionnaire = [
  {
    section: 'About your business',
    questions: [
      { id: 'q1', type: 'text', label: 'Business name and type', placeholder: 'e.g. Smith Hardware — retail store in Bridgeport, NE' },
      { id: 'q2', type: 'radio', label: 'How long have you owned this business?', options: ['Less than 5 years', '5–10 years', '11–20 years', 'More than 20 years'] },
      { id: 'q3', type: 'radio', label: 'How many employees do you have?', options: ['Just me', '2–5', '6–20', '20+'] },
    ],
  },
  {
    section: 'Your timeline and goals',
    questions: [
      { id: 'q4', type: 'radio', label: 'When are you thinking about stepping back?', options: ['Within 1–2 years', '3–5 years', '5–10 years', 'No specific timeline yet', 'Looking for a partner, not exiting'] },
      { id: 'q5', type: 'radio', label: 'Do you have a successor or buyer in mind?', options: ['Yes — a family member', 'Yes — a current employee', 'Yes — someone outside the business', 'Not yet — open to options', 'No — unsure the business can be sold'] },
    ],
  },
  {
    section: 'Your current readiness',
    questions: [
      { id: 'q6', type: 'radio', label: 'Do you have a written succession or exit plan?', options: ['Yes — documented and current', 'Partially — some things written down', 'No — plan is in my head only', 'No — not started yet'] },
      { id: 'q7', type: 'radio', label: 'Have you had a business valuation in the last 3 years?', options: ['Yes', 'No — but I have a rough sense', "No — I'm not sure how to find out"] },
      { id: 'q8', type: 'radio', label: 'Could your business run 30+ days without you?', options: ['Yes — strong staff and documented processes', 'Somewhat — it would struggle but manage', 'No — the business depends heavily on me'] },
    ],
  },
  {
    section: 'Contact information',
    questions: [
      { id: 'name', type: 'text', label: 'Your full name', placeholder: 'Full name' },
      { id: 'contact', type: 'text', label: 'Best phone or email', placeholder: 'Phone or email address' },
      { id: 'county', type: 'text', label: 'Which county are you in?', placeholder: 'e.g. Scotts Bluff, Box Butte, Dawes...' },
      { id: 'notes', type: 'textarea', label: 'Anything else you want us to know before we connect? (optional)', placeholder: 'Share anything that would help us assist you better...' },
    ],
  },
];

export default function SuccessionPlanning({ onBack }) {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [checked, setChecked] = useState({});
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    const { error } = await supabase.from('succession_responses').insert({
      user_id: profile?.id ?? null,
      business_name_type: answers.q1 ?? null,
      ownership_duration: answers.q2 ?? null,
      employee_count: answers.q3 ?? null,
      transition_timeline: answers.q4 ?? null,
      successor_identified: answers.q5 ?? null,
      succession_plan_status: answers.q6 ?? null,
      valuation_status: answers.q7 ?? null,
      operational_independence: answers.q8 ?? null,
      submitter_name: answers.name ?? null,
      contact_info: answers.contact ?? null,
      county: answers.county ?? null,
      notes: answers.notes ?? null,
    });
    setSubmitting(false);
    if (error) {
      setSubmitError('Something went wrong — please try again or call our office directly.');
    } else {
      setSubmitted(true);
    }
  }

  const checkCount = Object.values(checked).filter(Boolean).length;
  const pct = Math.round((checkCount / checklistItems.length) * 100);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'questionnaire', label: 'Readiness questionnaire' },
    { id: 'checklist', label: 'Pre-consultation checklist' },
    { id: 'resources', label: 'Resources' },
  ];

  const s = {
    page: { background: C.warm, minHeight: '100vh', fontFamily: FF.body },
    header: {
      background: C.navy,
      color: C.white,
      padding: '28px 32px 24px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '16px',
    },
    backBtn: {
      background: 'rgba(255,255,255,0.12)',
      border: '1px solid rgba(255,255,255,0.2)',
      color: C.white,
      borderRadius: '8px',
      padding: '6px 14px',
      fontSize: '13px',
      cursor: 'pointer',
      flexShrink: 0,
      marginTop: '4px',
      fontFamily: FF.body,
    },
    headerText: { flex: 1 },
    badge: {
      display: 'inline-block',
      background: C.gold,
      color: C.white,
      fontSize: '11px',
      fontWeight: 600,
      padding: '3px 10px',
      borderRadius: '20px',
      marginBottom: '8px',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
    },
    h1: { fontSize: '24px', fontFamily: FF.display, margin: '0 0 8px', fontWeight: 400, lineHeight: 1.3 },
    subhead: { fontSize: '14px', opacity: 0.8, margin: 0, lineHeight: 1.6 },
    stats: {
      background: C.slate,
      padding: '16px 32px',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
      gap: '12px',
    },
    stat: { textAlign: 'center' },
    statNum: { fontSize: '22px', fontWeight: 700, color: C.goldLight, fontFamily: FF.display },
    statLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '2px', lineHeight: 1.4 },
    tabs: {
      background: C.white,
      borderBottom: `1px solid ${C.sand}`,
      padding: '0 32px',
      display: 'flex',
      gap: '0',
      overflowX: 'auto',
    },
    tab: (active) => ({
      padding: '14px 20px',
      fontSize: '13px',
      fontWeight: active ? 600 : 400,
      color: active ? C.gold : C.slate,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      background: 'none',
      border: 'none',
      borderBottom: active ? `2px solid ${C.gold}` : '2px solid transparent',
      fontFamily: FF.body,
    }),
    body: { padding: '28px 32px', maxWidth: '820px', margin: '0 auto' },
    card: {
      background: C.white,
      border: `1px solid ${C.sand}`,
      borderRadius: '12px',
      padding: '20px 24px',
      marginBottom: '16px',
    },
    cardH: { fontSize: '15px', fontWeight: 600, color: C.navy, marginBottom: '6px' },
    cardP: { fontSize: '13px', color: '#5a5a5a', lineHeight: 1.6, margin: 0 },
    grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginBottom: '20px' },
    sectionH: { fontSize: '18px', fontFamily: FF.display, fontWeight: 400, color: C.navy, marginBottom: '6px', marginTop: '24px' },
    sectionSub: { fontSize: '13px', color: '#5a5a5a', lineHeight: 1.6, marginBottom: '16px' },
    stepRow: { display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '14px' },
    stepNum: {
      width: '30px', height: '30px', borderRadius: '50%',
      background: C.gold, color: C.white,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '13px', fontWeight: 700, flexShrink: 0, marginTop: '2px',
    },
    tlGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '24px' },
    qBlock: { background: C.white, border: `1px solid ${C.sand}`, borderRadius: '10px', padding: '16px 20px', marginBottom: '12px' },
    qLabel: { fontSize: '14px', fontWeight: 600, color: C.navy, marginBottom: '10px', display: 'block' },
    radio: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#5a5a5a', marginBottom: '6px', cursor: 'pointer' },
    textInput: {
      width: '100%', fontSize: '13px', padding: '9px 12px',
      border: `1px solid ${C.sand}`, borderRadius: '8px',
      background: C.warm, color: C.navy, fontFamily: FF.body,
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%', fontSize: '13px', padding: '9px 12px',
      border: `1px solid ${C.sand}`, borderRadius: '8px',
      background: C.warm, color: C.navy, fontFamily: FF.body,
      resize: 'vertical', minHeight: '80px', boxSizing: 'border-box',
    },
    submitBtn: {
      background: C.gold, color: C.white, border: 'none',
      borderRadius: '8px', padding: '13px 28px',
      fontSize: '14px', fontWeight: 600, cursor: 'pointer',
      width: '100%', marginTop: '8px', fontFamily: FF.body,
    },
    sectionTag: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.gold, marginBottom: '12px', marginTop: '24px', display: 'block' },
    clItem: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderBottom: `1px solid ${C.sand}`, fontSize: '13px', color: '#5a5a5a' },
    chkBox: (on) => ({
      width: '18px', height: '18px', borderRadius: '4px',
      border: `1.5px solid ${on ? C.gold : C.sand}`,
      background: on ? C.gold : C.white,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: 'pointer', flexShrink: 0, marginTop: '1px',
      color: C.white, fontSize: '11px', fontWeight: 700,
    }),
    resRow: { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px 0', borderBottom: `1px solid ${C.sand}` },
    resIcon: {
      width: '36px', height: '36px', borderRadius: '8px',
      background: C.cream, color: C.slate,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: '12px', fontWeight: 700, flexShrink: 0,
    },
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack}>← Back</button>
        )}
        <div style={s.headerText}>
          <span style={s.badge}>PADD Business Services</span>
          <h1 style={s.h1}>Business Succession Planning</h1>
          <p style={s.subhead}>
            Free, confidential support to help Panhandle business owners protect their life's work and keep jobs in the region.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div style={s.stats}>
        {[
          { num: '46%', label: 'of rural businesses have no succession plan' },
          { num: '87%', label: 'of new owners maintained or grew employment' },
          { num: '<30%', label: 'of family firms survive to the 2nd generation' },
          { num: 'Free', label: 'confidential guidance from PADD advisors' },
        ].map((st) => (
          <div key={st.num} style={s.stat}>
            <div style={s.statNum}>{st.num}</div>
            <div style={s.statLabel}>{st.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={s.tabs}>
        {tabs.map((t) => (
          <button key={t.id} style={s.tab(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab bodies */}
      <div style={s.body}>

        {/* ── OVERVIEW ── */}
        {activeTab === 'overview' && (
          <>
            <h2 style={s.sectionH}>Why this matters in the Panhandle</h2>
            <p style={s.sectionSub}>
              PADD's 2026–2031 CEDS identifies business succession as the region's most urgent and most consistently unaddressed economic challenge. Retiring owners without buyers or a plan means community economic collapse — one business at a time. We're doing something about it.
            </p>

            <div style={s.grid2}>
              {[
                { title: 'Keep jobs local', desc: 'A planned transition keeps employees working, customers served, and dollars circulating. Unplanned closures hurt everyone.' },
                { title: 'Maximize your value', desc: 'Owners who plan ahead capture full value. Those who don\'t often sell too cheap — or simply shut the door.' },
                { title: 'Protect your legacy', desc: 'Your values, brand, and community ties can carry forward — on your terms — with the right plan.' },
                { title: 'More options than you think', desc: 'Family transfer, employee buyout, ESOP, third-party sale — we help you find the right path.' },
              ].map((c) => (
                <div key={c.title} style={s.card}>
                  <div style={s.cardH}>{c.title}</div>
                  <p style={s.cardP}>{c.desc}</p>
                </div>
              ))}
            </div>

            <h2 style={s.sectionH}>How PADD's support works</h2>
            <p style={s.sectionSub}>A simple, confidential process — at no cost to you.</p>
            {steps.map((st) => (
              <div key={st.num} style={s.stepRow}>
                <div style={s.stepNum}>{st.num}</div>
                <div>
                  <div style={{ ...s.cardH, marginBottom: '3px' }}>{st.title}</div>
                  <p style={s.cardP}>{st.desc}</p>
                </div>
              </div>
            ))}

            <h2 style={s.sectionH}>Planning timeline</h2>
            <p style={s.sectionSub}>Earlier is always better — but it's never too late to start.</p>
            <div style={s.tlGrid}>
              {timeline.map((tl) => (
                <div key={tl.label} style={{ background: tl.color, borderRadius: '10px', padding: '14px 16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: tl.textColor, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{tl.label}</div>
                  <div style={{ fontSize: '12px', color: '#5a5a5a', lineHeight: 1.6 }}>{tl.items}</div>
                </div>
              ))}
            </div>

            <div style={{ textAlign: 'center', marginTop: '8px' }}>
              <button style={s.submitBtn} onClick={() => setActiveTab('questionnaire')}>
                Get started — fill out the readiness questionnaire →
              </button>
            </div>
          </>
        )}

        {/* ── QUESTIONNAIRE ── */}
        {activeTab === 'questionnaire' && !submitted && (
          <>
            <h2 style={s.sectionH}>Readiness questionnaire</h2>
            <p style={s.sectionSub}>
              This confidential questionnaire helps PADD understand your situation and connect you with the right support. All responses are private. A PADD advisor will follow up within 5 business days.
            </p>

            {questionnaire.map((sec) => (
              <div key={sec.section}>
                <span style={s.sectionTag}>{sec.section}</span>
                {sec.questions.map((q) => (
                  <div key={q.id} style={s.qBlock}>
                    <span style={s.qLabel}>{q.label}</span>
                    {q.type === 'radio' && q.options.map((opt) => (
                      <label key={opt} style={s.radio}>
                        <input
                          type="radio"
                          name={q.id}
                          value={opt}
                          checked={answers[q.id] === opt}
                          onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt }))}
                          style={{ accentColor: C.gold }}
                        />
                        {opt}
                      </label>
                    ))}
                    {q.type === 'text' && (
                      <input
                        type="text"
                        style={s.textInput}
                        placeholder={q.placeholder}
                        value={answers[q.id] || ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      />
                    )}
                    {q.type === 'textarea' && (
                      <textarea
                        style={s.textarea}
                        placeholder={q.placeholder}
                        value={answers[q.id] || ''}
                        onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}

            {submitError && (
              <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '8px' }}>{submitError}</p>
            )}
            <button style={{ ...s.submitBtn, opacity: submitting ? 0.6 : 1 }} onClick={handleSubmit} disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit — connect me with a PADD advisor'}
            </button>
          </>
        )}

        {activeTab === 'questionnaire' && submitted && (
          <div style={{ ...s.card, textAlign: 'center', padding: '40px 24px' }}>
            <div style={{ fontSize: '36px', marginBottom: '12px' }}>✓</div>
            <div style={{ ...s.cardH, fontSize: '18px', marginBottom: '8px' }}>Thank you — we'll be in touch within 5 business days.</div>
            <p style={s.cardP}>A PADD advisor will review your responses and reach out to schedule your free confidential consultation. You can also call our office directly at any time.</p>
            <button style={{ ...s.submitBtn, marginTop: '20px', width: 'auto', padding: '11px 28px' }} onClick={() => setActiveTab('resources')}>
              Explore local resources →
            </button>
          </div>
        )}

        {/* ── CHECKLIST ── */}
        {activeTab === 'checklist' && (
          <>
            <h2 style={s.sectionH}>Pre-consultation checklist</h2>
            <p style={s.sectionSub}>Check off what you already have — this tells you and your PADD advisor where to focus.</p>
            <div style={s.card}>
              {checklistItems.map((item, i) => (
                <div key={i} style={{ ...s.clItem, borderBottom: i === checklistItems.length - 1 ? 'none' : `1px solid ${C.sand}` }}>
                  <div
                    style={s.chkBox(!!checked[i])}
                    onClick={() => setChecked((c) => ({ ...c, [i]: !c[i] }))}
                  >
                    {checked[i] ? '✓' : ''}
                  </div>
                  <span style={{ lineHeight: 1.6 }}>{item}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#5a5a5a', marginBottom: '6px' }}>
                <span>Your readiness</span>
                <span style={{ fontWeight: 600 }}>{checkCount} / {checklistItems.length}</span>
              </div>
              <div style={{ background: C.sand, borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
                <div style={{ height: '8px', width: `${pct}%`, background: C.gold, borderRadius: '999px', transition: 'width 0.4s' }} />
              </div>
              <p style={{ fontSize: '12px', color: '#9a9a9a', marginTop: '8px' }}>PADD can help with every unchecked item above.</p>
            </div>

            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button style={s.submitBtn} onClick={() => setActiveTab('questionnaire')}>
                Ready to get started? Fill out the questionnaire →
              </button>
            </div>
          </>
        )}

        {/* ── RESOURCES ── */}
        {activeTab === 'resources' && (
          <>
            <h2 style={s.sectionH}>Local and regional resources</h2>
            <p style={s.sectionSub}>PADD works alongside these organizations to provide comprehensive succession support across the Nebraska Panhandle.</p>
            <div style={s.card}>
              {resources.map((r, i) => (
                <div key={r.initials} style={{ ...s.resRow, borderBottom: i === resources.length - 1 ? 'none' : `1px solid ${C.sand}` }}>
                  <div style={s.resIcon}>{r.initials}</div>
                  <div>
                    <div style={{ ...s.cardH, marginBottom: '3px' }}>{r.name}</div>
                    <p style={s.cardP}>{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

      </div>
    </div>
  );
}
